import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useOrder } from '../hooks/useOrders';
import { Table, TableBody, TableCell, TableHead, TableRow, Container, Paper, Button } from '@mui/material';
import { Order } from '../../types/Order';
import EditMemoModal from '../../components/editMemoModal';
import { supabase } from '../../configs/supabase';

const OrderDetail = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const location = useLocation();
  const { fetchOrderByNumber, error } = useOrder();
  const [orderDetails, setOrderDetails] = useState<Order[]>([]);
  const [currentMemo, setCurrentMemo] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { itemCount, itemSum } = location.state || {};

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (orderId) {
        const orders = await fetchOrderByNumber(Number(orderId));
        setOrderDetails(orders);
      }
    };

    fetchOrderDetails();
  }, [orderId, fetchOrderByNumber]);

  useEffect(() => {
    if (orderDetails.length > 0) {
      setCurrentMemo(orderDetails[0].memo || '');
    }
  }, [orderDetails]);

  const handleEditMemo = (memo: string) => {
    setCurrentMemo(memo);
    setIsModalOpen(true);
  };

  const handleSaveMemo = async (memo: string) => {
    if (memo) {
      const { error } = await supabase
        .from('order')
        .update({ memo })
        .eq('受注番号', orderId);
      if (error) {
        console.error("メモの更新失敗: ", error);
      } else {
        // 更新後にデータを再取得
        const orders = await fetchOrderByNumber(Number(orderId));
        setOrderDetails(orders);
      }
    }
  };

  return (
    <Container>
      <h2>受注詳細</h2>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {orderDetails.length > 0 && (
        <Paper style={{ padding: '16px', marginBottom: '16px' }}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>受注番号</TableCell>
                <TableCell>{orderDetails[0].受注番号}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>受注日時</TableCell>
                <TableCell>{orderDetails[0].注文日.replace("T", " ")}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>納品希望日</TableCell>
                <TableCell>{orderDetails[0].納品希望日}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>顧客コード / 顧客名</TableCell>
                <TableCell>{orderDetails[0].顧客コード} / {orderDetails[0].顧客名}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>配送先コード / 配送先名</TableCell>
                <TableCell>{orderDetails[0].配送先コード} / {orderDetails[0].配送先名}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>配送先住所</TableCell>
                <TableCell>{orderDetails[0].配送先住所}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>商品点数 / 数量合計</TableCell>
                <TableCell>{itemCount} / {itemSum}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>連絡事項</TableCell>
                <TableCell>{orderDetails[0]["コメント（備考）"]}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>メモ</TableCell>
                <TableCell>
                  {currentMemo && 
                    <>{currentMemo}<br/></>
                  }
                  <Button variant="outlined" color="primary" onClick={() => handleEditMemo(orderDetails[0].memo)}>
                    メモを編集
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>商品コード</TableCell>
            <TableCell>商品名</TableCell>
            <TableCell>数量</TableCell>
            <TableCell>単位</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orderDetails.map((order, index) => (
            <TableRow key={index}>
              <TableCell>{order.商品コード}</TableCell>
              <TableCell>{order.商品名}</TableCell>
              <TableCell>{order.数量}</TableCell>
              <TableCell>{order.単位}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <EditMemoModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveMemo}
        initialMemo={currentMemo}
      />
    </Container>
  );
};

export default OrderDetail;
